import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useTheme } from '../../ThemeContext';
import '../OfflineNotification/OfflineNotification.css';
import { getConnectivityState, setDeviceOnline, subscribeConnectivity } from '../../services/connectivityService';

export const RECONNECTED_FEEDBACK_DURATION = 3000;
export const CONNECTIVITY_STATES = Object.freeze({
  OFFLINE: 'offline',
  RECONNECTED: 'reconnected-feedback',
  NORMAL: 'normal',
});

const ConnectivityStatus = () => {
  const initialState = navigator.onLine ? CONNECTIVITY_STATES.NORMAL : CONNECTIVITY_STATES.OFFLINE;
  const [connectivityState, setConnectivityState] = useState(initialState);
  const stateRef = useRef(initialState);
  const timerRef = useRef(null);
  const { colors, setStatusBarColor, restoreStatusBarColor } = useTheme();
  const connectivity = useSyncExternalStore(subscribeConnectivity, getConnectivityState, getConnectivityState);

  const clearFeedbackTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const transitionTo = useCallback((nextState) => {
    stateRef.current = nextState;
    setConnectivityState(nextState);
  }, []);

  useEffect(() => {
    const handleOffline = () => {
      setDeviceOnline(false);
      clearFeedbackTimer();
      transitionTo(CONNECTIVITY_STATES.OFFLINE);
    };
    const handleOnline = () => {
      setDeviceOnline(true);
      if (stateRef.current !== CONNECTIVITY_STATES.OFFLINE && navigator.onLine) return;
      clearFeedbackTimer();
      transitionTo(CONNECTIVITY_STATES.RECONNECTED);
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null;
        transitionTo(CONNECTIVITY_STATES.NORMAL);
      }, RECONNECTED_FEEDBACK_DURATION);
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      if (!navigator.onLine) handleOffline();
      else if (stateRef.current === CONNECTIVITY_STATES.OFFLINE) handleOnline();
    };

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      clearFeedbackTimer();
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [clearFeedbackTimer, transitionTo]);

  useEffect(() => {
    if (connectivityState === CONNECTIVITY_STATES.OFFLINE) setStatusBarColor(colors.dangerColor);
    else if (connectivityState === CONNECTIVITY_STATES.RECONNECTED) setStatusBarColor(colors.successColor);
    else restoreStatusBarColor();
  }, [colors.dangerColor, colors.successColor, connectivityState, restoreStatusBarColor, setStatusBarColor]);

  const serviceUnavailable = connectivity.serviceAvailable === false && connectivityState === CONNECTIVITY_STATES.NORMAL;
  const visible = connectivityState !== CONNECTIVITY_STATES.NORMAL || serviceUnavailable;
  const offline = connectivityState === CONNECTIVITY_STATES.OFFLINE;

  useEffect(() => {
    document.body.classList.toggle('nf-connectivity-banner-visible', visible);
    return () => document.body.classList.remove('nf-connectivity-banner-visible');
  }, [visible]);

  return (
    <div
      className={`offline-notification${visible ? ' offline-notification--visible' : ''} offline-notification--${offline ? 'offline' : 'online'}`}
      role="status"
      aria-live="polite"
      aria-atomic="true"
      data-connectivity-state={connectivityState}
    >
      <span className="offline-notification__content">
        <span className="offline-notification__indicator" aria-hidden="true" />
        <span>{offline
          ? 'Sem conexão. Dados exibidos podem estar desatualizados; alterações exigem internet.'
          : serviceUnavailable
            ? 'Serviço indisponível. Dados exibidos podem estar desatualizados; nenhuma alteração foi confirmada.'
            : 'Conexão restabelecida. Validando dados com os serviços.'}</span>
      </span>
    </div>
  );
};

export default ConnectivityStatus;
